import React from 'react';
import { ArrowLeft, Phone, MoreVertical, Paperclip, Camera, Mic, Smile, Check, CheckCheck } from 'lucide-react';

interface MobilePreviewProps {
  headerName?: string;
  isBusinessAccount?: boolean;
  messageBody?: string;
  headerImageUrl?: string;
  buttons?: string[];
  messageTime?: string;
  status?: 'sent' | 'delivered' | 'read';
}

export default function MobilePreview({
  headerName = "Acme Events",
  isBusinessAccount = true,
  messageBody = "Hello {{1}},\n\nYou are warmly invited to our exclusive event of the year.\n\nThank you!",
  headerImageUrl = "https://images.unsplash.com/photo-1551818255-e6e10975bc17?q=80&w=1973&auto=format&fit=crop",
  buttons = ["✓ Yes, I'll attend", "✕ Unable to attend"],
  messageTime = "9:41 AM",
  status = "read"
}: MobilePreviewProps) {

  // Process message body to render paragraphs nicely
  const formattedBody = messageBody.split('\n').map((line, i) => (
    <React.Fragment key={i}>
      {line}
      {i !== messageBody.split('\n').length - 1 && <br />}
    </React.Fragment>
  ));

  return (
    <div className="relative mx-auto border-gray-800 dark:border-gray-800 bg-gray-800 border-[14px] rounded-[2.5rem] h-[600px] w-[300px] shadow-xl overflow-hidden">
      {/* Notch */}
      <div className="absolute top-0 inset-x-0 h-6 bg-gray-800 rounded-b-3xl w-40 mx-auto z-20"></div>

      {/* Screen Content */}
      <div className="bg-[#EFEAE2] h-full w-full relative flex flex-col font-sans">
        
        {/* iOS Status Bar */}
        <div className="flex justify-between items-center px-6 pt-2 pb-1 bg-[#005e54] text-white text-xs font-semibold z-10">
          <span>9:41</span>
          <div className="flex space-x-1 items-center">
            {/* Minimal icons for signal/wifi/battery */}
            <svg viewBox="0 0 24 24" className="h-3 w-3 fill-current"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
            <svg viewBox="0 0 24 24" className="h-3 w-3 fill-current"><path d="M12 4C7.31 4 3.07 5.9 0 8.98L12 21 24 8.98C20.93 5.9 16.69 4 12 4z"/></svg>
            <svg viewBox="0 0 24 24" className="h-3 w-3 fill-current"><path d="M15.67 4H14V2h-4v2H8.33C7.6 4 7 4.6 7 5.33v15.33C7 21.4 7.6 22 8.33 22h7.33c.74 22 1.34 21.4 1.34 20.67V5.33C17 4.6 16.4 4 15.67 4z"/></svg>
          </div>
        </div>

        {/* WhatsApp Header */}
        <div className="bg-[#005e54] flex items-center px-2 py-2 text-white shadow-sm z-10">
          <div className="flex items-center space-x-1 cursor-pointer">
            <ArrowLeft className="w-5 h-5" />
            <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center overflow-hidden">
               <span className="text-gray-600 text-xs font-bold text-center leading-none">
                 {headerName.substring(0,2).toUpperCase()}
               </span>
            </div>
          </div>
          <div className="ml-2 flex-1 overflow-hidden">
            <div className="flex items-center space-x-1">
              <h2 className="font-semibold text-[15px] truncate leading-tight">{headerName}</h2>
              {isBusinessAccount && (
                <svg viewBox="0 0 16 16" className="w-3 h-3 text-green-400 fill-current">
                  <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
                </svg>
              )}
            </div>
            {isBusinessAccount && <p className="text-[11px] text-gray-200 leading-tight">Business Account</p>}
          </div>
          <div className="flex space-x-3 items-center">
             <Phone className="w-5 h-5" />
             <MoreVertical className="w-5 h-5" />
          </div>
        </div>

        {/* Chat Area (with background pattern simulation) */}
        <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-[#EFEAE2] pb-16" style={{ backgroundImage: "url('https://i.pinimg.com/originals/8f/ba/cb/8fbacbd464e996966eb9d4a6b7a9c21e.jpg')", backgroundSize: 'cover', backgroundBlendMode: 'overlay', backgroundColor: 'rgba(239, 234, 226, 0.9)' }}>
          
          <div className="flex justify-center">
            <span className="bg-white/80 text-gray-600 text-xs px-2 py-1 rounded-lg shadow-sm">Today</span>
          </div>

          {/* Incoming Template Message */}
          <div className="flex flex-col items-start w-full">
            <div className="bg-white rounded-lg rounded-tl-none shadow-sm max-w-[90%] overflow-hidden">
              
              {/* Header Image */}
              {headerImageUrl && (
                <div className="w-full h-32 bg-gray-200">
                  <img src={headerImageUrl} alt="Header" className="w-full h-full object-cover" />
                </div>
              )}
              
              {/* Message Body */}
              <div className="p-3">
                <div className="text-[14px] text-[#111b21] leading-snug whitespace-pre-wrap font-sans">
                  {formattedBody}
                </div>
                
                {/* Time and Status */}
                <div className="flex justify-end items-center mt-1 space-x-1">
                  <span className="text-[11px] text-gray-500">{messageTime}</span>
                  {status === 'read' ? (
                     <CheckCheck className="w-3.5 h-3.5 text-blue-500" />
                  ) : status === 'delivered' ? (
                     <CheckCheck className="w-3.5 h-3.5 text-gray-400" />
                  ) : (
                     <Check className="w-3.5 h-3.5 text-gray-400" />
                  )}
                </div>
              </div>
              
              {/* Quick Reply Buttons */}
              {buttons && buttons.length > 0 && (
                <div className="border-t border-gray-100 flex flex-col w-full bg-white">
                  {buttons.map((btn, idx) => (
                    <button 
                      key={idx} 
                      className={`py-2.5 text-[14px] font-medium w-full text-center hover:bg-gray-50 transition ${
                         btn.includes('Yes') || btn.includes('attend') ? 'text-[#00A884]' : 
                         btn.includes('Unable') || btn.includes('No') ? 'text-red-500' : 'text-[#00A884]'
                      } ${idx !== buttons.length - 1 ? 'border-b border-gray-100' : ''}`}
                    >
                      {btn}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
        </div>

        {/* Input Footer */}
        <div className="absolute bottom-0 inset-x-0 bg-[#F0F2F5] p-2 flex items-center space-x-2 z-10">
          <div className="flex-1 bg-white rounded-full flex items-center px-3 py-1.5 shadow-sm h-10">
            <Smile className="w-5 h-5 text-gray-500 mr-2" />
            <span className="text-gray-400 flex-1 text-[15px]">Message</span>
            <Paperclip className="w-5 h-5 text-gray-500 mr-3" />
            <Camera className="w-5 h-5 text-gray-500" />
          </div>
          <div className="w-10 h-10 bg-[#00A884] rounded-full flex items-center justify-center text-white shadow-sm flex-shrink-0">
            <Mic className="w-5 h-5" />
          </div>
        </div>

      </div>
    </div>
  );
}
